// Required Core Sections Utility
// --------------------------------
// Single source of truth for "which core report sections are required for
// this patient's assessment workflow?"
//
// This centralises a business rule that was previously duplicated across
// autoPatientReport.js, autoCoverPage.js, DocumentsDashboard.js, and
// AdditionalDocuments.js.  All four consumers now import from here,
// eliminating drift risk and ensuring consistent gating for:
//   - CP/PR auto-regeneration (auto-helpers)
//   - Section visibility on the Documents Dashboard
//   - Patient Report unlock in AdditionalDocuments
//
// Business rules (confirmed):
//   Early exit (one-clinician SLaM, assessment ended after the CI): CI(1) only
//     — the "early exit" Patient Report is built from the CI report alone
//       (see EARLY_EXIT_AFTER_CI in the SLaM Master template); SSI, DD and
//       PAD are locked and never happen unless the assessment is resumed.
//   ADHD flow (any org):                CI(1), SSI(2), DD(3), PAD(4)
//   Not-ADHD + SSI-always (SLaM etc.): CI(1), SSI(2), DD(3), PAD(4)
//     — clinician always conducts a shortened PAD (section 1 only)
//   Not-ADHD + standard orgs:          CI(1), DD(3) only
//
// The ADHD-flow question is answered by isAdhdAssessmentFlow, which
// handles both hierarchical (PPD-based) and flat (DD-outcome-based)
// diagnostic models internally.

import { resolveWorkflowConfig } from '../hooks/useWorkflowConfig';
import { isAdhdAssessmentFlow } from './conditionChecks';

// Fixed mapping from report section number (used in 6_manageReports.sections[N])
// to the approval boolean field on the manageReports object.
export const SECTION_TO_APPROVED_KEY = {
  '1': 'ciReportApproved',
  '2': 'ssiReportApproved',
  '3': 'ddReportApproved',
  '4': 'padReportApproved',
};

// Maps DocumentsDashboard section IDs to their report section numbers.
// These are stable regardless of moduleOrder (SSI-first vs CI-first) —
// they represent Firestore data-model structure, not UI ordering.
export const DASHBOARD_ID_TO_SECTION = {
  clinicalInterview: '1',
  semiStructuredInterview: '2',
  diagnosticDecision: '3',
  postAssessmentDiscussion: '4',
};

/**
 * Determine which core report sections are required for this patient's
 * assessment workflow.
 *
 * The result drives:
 *   - Whether CP/PR auto-regeneration waits for a section's approval
 *   - Whether a section row is visible on the Documents Dashboard
 *   - Whether the Patient Report is unlockable in AdditionalDocuments
 *
 * @param {Object} patientDataModel - The patient's Firestore document
 * @param {string} organisation - The organisation name (for workflow config resolution)
 * @returns {string[]} Array of required section numbers, e.g. ['1','2','3','4']
 */
export const getRequiredCoreSections = (patientDataModel, organisation) => {
  const wf = resolveWorkflowConfig(patientDataModel, { organisation });

  // Early exit: the Lead ended the assessment at the end of the Clinical
  // Interview (one-clinician SLaM / 3 Borough). No SSI, DD or PAD will be
  // conducted, so requiring them would lock the Patient Report forever.
  // The CI report's own approval already covers the Assessment Conclusion
  // paragraph (ciReportApproved is derived from every paragraph in the CI
  // review being approved), so CI alone is the complete gate. This is
  // checked before the ADHD-flow rules because isAdhdAssessmentFlow for a
  // flat-DF SLaM patient reads the DD outcome, which does not exist here.
  // "Resume assessment" deletes the decision, so the set reverts to the
  // normal rules below and the Patient Report re-locks until SSI/DD/PAD are
  // approved.
  if (wf.assessmentEndedAfterCI) return ['1'];

  const adhdFlow = isAdhdAssessmentFlow(patientDataModel, wf);

  // ADHD flow: all 4 core sections required
  if (adhdFlow) return ['1', '2', '3', '4'];

  // SSI-always orgs (SLaM, 3 Borough): PAD is always conducted even for
  // not-ADHD patients (shortened to section 1 only), so all 4 sections
  // are required before CP/PR can finalize.
  if (wf.isSSIAlwaysExpected) return ['1', '2', '3', '4'];

  // Standard orgs, not-ADHD: only CI and DD (SSI/PAD not conducted)
  return ['1', '3'];
};

/**
 * Check whether all required core sections have been approved.
 *
 * @param {Object} manageReports - The 6_manageReports object from the patient document
 * @param {string[]} requiredSections - Output of getRequiredCoreSections
 * @returns {boolean}
 */
export const areAllRequiredSectionsApproved = (manageReports, requiredSections) => {
  return requiredSections.every((n) => !!manageReports[SECTION_TO_APPROVED_KEY[n]]);
};

/**
 * Convenience helper for the Documents Dashboard: check whether a given
 * dashboard section ID is in the required set.
 *
 * Returns true for unknown IDs (e.g. QbCheck, Rating Scales) so that
 * non-core sections are never accidentally hidden by this check.
 *
 * @param {string} dashboardId - e.g. 'semiStructuredInterview', 'postAssessmentDiscussion'
 * @param {string[]} requiredSections - Output of getRequiredCoreSections
 * @returns {boolean}
 */
export const isSectionRequired = (dashboardId, requiredSections) => {
  const sectionNum = DASHBOARD_ID_TO_SECTION[dashboardId];
  return sectionNum ? requiredSections.includes(sectionNum) : true;
};
